import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { isAuthenticated } from './utils/auth';
import { userService } from './services/api';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AIRecommendationsDashboard from './pages/AIRecommendationsDashboard';
import AccountPage from './pages/AccountPage';
import TradingPage from './pages/TradingPage';
import ComprehensiveAIDashboard from './pages/ComprehensiveAIDashboard';
import AutoTradingAnalysisPage from './pages/AutoTradingAnalysisPage';
import AIRecommendationsPage from './pages/AIRecommendationsPage';
import AIAnalysisPage from './pages/AIAnalysisPage';
import SettingsPage from './pages/SettingsPage';
import UserStockAddPage from './pages/UserStockAddPage';
import PortfolioPriorityPage from './pages/PortfolioPriorityPage';
import SectorManagementPage from './pages/SectorManagementPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import BoxPortfolio from './pages/BoxPortfolio';
import OnboardingPage from './pages/OnboardingPage';
import PerformancePage from './pages/PerformancePage';
import UsPortfolioPage from './pages/UsPortfolioPage';

// Loading 컴포넌트
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">로딩 중...</p>
    </div>
  </div>
);

// ── 미동의 사용자 팝업 ───────────────────────────────────────────────────────
const DisclaimerModal = ({ disclaimer, onAgree, onClose }) => {
  const [expanded, setExpanded] = useState(false);
  const [isAgreeing, setIsAgreeing] = useState(false);

  const handleAgree = async () => {
    setIsAgreeing(true);
    try {
      await onAgree(disclaimer.version);
    } finally {
      setIsAgreeing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* 헤더 */}
        <div className="flex items-center gap-3 p-5 bg-amber-50 border-b border-amber-200 rounded-t-2xl">
          <AlertTriangle size={22} className="text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-base">투자 책임 확인서 동의 필요</h3>
            <p className="text-xs text-gray-500 mt-0.5">서비스 이용을 위해 동의가 필요합니다</p>
          </div>
        </div>

        {/* 본문 */}
        <div className="p-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 overflow-hidden mb-4">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <span>확인서 전문 보기</span>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expanded && (
              <div className="px-4 pb-4 max-h-56 overflow-y-auto border-t border-amber-200">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed font-sans pt-3">
                  {disclaimer.content}
                </pre>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mb-5">
            투자로 인한 <strong>모든 손익의 책임은 본인에게 있음</strong>을 확인하고 동의합니다.
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleAgree}
              disabled={isAgreeing}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isAgreeing ? '처리 중...' : '동의하고 계속하기'}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            동의하지 않으면 서비스 이용이 제한될 수 있습니다
          </p>
        </div>
      </div>
    </div>
  );
};

// Protected Route Component with Onboarding Check
const ProtectedRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (hasChecked.current) return;
      if (!isAuthenticated()) { setIsChecking(false); return; }
      hasChecked.current = true;
      try {
        const response = await userService.getApiKeysStatus();
        const status = response.data;
        const isConfigured = status.kis_configured && status.account_number_configured;
        if (!isConfigured) setNeedsOnboarding(true);
      } catch (error) {
        console.error('API 키 상태 확인 실패:', error);
      } finally {
        setIsChecking(false);
      }
    };
    checkOnboardingStatus();
  }, []);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (isChecking) return <LoadingSpinner />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  if (!isAuthenticated()) return children;
  return <Navigate to="/account" replace />;
};

const OnboardingRoute = () => {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return <OnboardingPage />;
};

function App() {
  const [disclaimerInfo, setDisclaimerInfo] = useState(null);

  // 로그인 상태일 때 약관 동의 여부 확인
  useEffect(() => {
    if (!isAuthenticated()) return;
    userService.checkNeedAgreement()
      .then(res => {
        if (res.data?.need_agreement) setDisclaimerInfo(res.data);
      })
      .catch(() => {});
  }, []);

  const handleAgree = async (version) => {
    try {
      await userService.agreeDisclaimer(version);
      setDisclaimerInfo(null);
    } catch (e) {
      console.error('동의 저장 실패:', e);
    }
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login"        element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"     element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/verify-email" element={<PublicRoute><EmailVerificationPage /></PublicRoute>} />

          {/* Onboarding */}
          <Route path="/onboarding" element={<OnboardingRoute />} />

          {/* Protected Routes */}
          <Route path="/account"            element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/trading"            element={<ProtectedRoute><TradingPage /></ProtectedRoute>} />
          <Route path="/portfolio-priority" element={<ProtectedRoute><PortfolioPriorityPage /></ProtectedRoute>} />
          <Route path="/dashboard"          element={<ProtectedRoute><AIRecommendationsDashboard /></ProtectedRoute>} />
          <Route path="/box-portfolio"      element={<ProtectedRoute><BoxPortfolio /></ProtectedRoute>} />
          <Route path="/us-portfolio"       element={<ProtectedRoute><UsPortfolioPage /></ProtectedRoute>} />

          {/* AI Analysis */}
          <Route path="/comprehensive-ai"      element={<ProtectedRoute><ComprehensiveAIDashboard /></ProtectedRoute>} />
          <Route path="/auto-trading-analysis" element={<ProtectedRoute><AutoTradingAnalysisPage /></ProtectedRoute>} />
          <Route path="/ai-recommendations"    element={<ProtectedRoute><AIRecommendationsPage /></ProtectedRoute>} />
          <Route path="/ai-analysis"           element={<ProtectedRoute><AIAnalysisPage /></ProtectedRoute>} />

          {/* Other */}
          <Route path="/add-stock"          element={<ProtectedRoute><UserStockAddPage /></ProtectedRoute>} />
          <Route path="/settings"           element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/delete-account"     element={<ProtectedRoute><AccountDeletionPage /></ProtectedRoute>} />
          <Route path="/sector-management"  element={<ProtectedRoute><SectorManagementPage /></ProtectedRoute>} />
          <Route path="/performance"        element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />

          {/* Default & 404 */}
          <Route path="/" element={<Navigate to="/account" replace />} />
          <Route path="*" element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-8">페이지를 찾을 수 없습니다.</p>
                <a href="/account" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  계좌로 돌아가기
                </a>
              </div>
            </div>
          } />
        </Routes>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#363636', color: '#fff' },
            success: { duration: 3000 },
          }}
        />
        {/* 미동의 사용자 팝업 */}
        {disclaimerInfo && (
          <DisclaimerModal
            disclaimer={disclaimerInfo}
            onAgree={handleAgree}
            onClose={() => setDisclaimerInfo(null)}
          />
        )}
      </div>
    </Router>
  );
}

export default App;
